import { Combobox, FieldError, type ComboboxOption } from '@volee/ui'
import * as React from 'react'
import {
  useController,
  type FieldPath,
  type FieldValues,
  type UseFormReturn,
} from 'react-hook-form'

// `Combobox` renders its own `<Label>`, so it stays bare instead of nested inside `FormField`,
// which would draw a second one; `FieldError` is the same line `FormField` draws.

/** Case- and accent-insensitive: typing "cote" should still find "Côte d'Ivoire". */
export function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

function matching(options: readonly ComboboxOption[], typed: string): readonly ComboboxOption[] {
  const needle = folded(typed.trim())
  if (needle === '') return options
  return options.filter((option) => folded(option.label).includes(needle))
}

export interface ComboboxFieldProps<TFields extends FieldValues> {
  readonly form: UseFormReturn<TFields>
  readonly name: FieldPath<TFields>
  readonly label: string
  readonly options: readonly ComboboxOption[]
  readonly emptyLabel: string
  readonly loadingLabel: string
  readonly clearLabel: string
  readonly placeholder?: string
  readonly error?: string
  readonly pending?: boolean
}

/** `useController`, not `setValue`: the reason is `SelectField`'s — an error the server put
 * on this field would otherwise survive a correct selection and block every next submit. */
export function ComboboxField<TFields extends FieldValues>({
  form,
  name,
  label,
  options,
  emptyLabel,
  loadingLabel,
  clearLabel,
  placeholder,
  error,
  pending = false,
}: ComboboxFieldProps<TFields>) {
  const { field } = useController({ control: form.control, name })
  const chosen = String(field.value ?? '')
  const chosenLabel = options.find((option) => option.value === chosen)?.label ?? ''
  const [typed, setTyped] = React.useState<string | null>(null)
  const fieldId = React.useId()
  const errorId = `${fieldId}-error`

  // `null` means "not typing": the input then shows the chosen option's label, so a value
  // that arrived from the server appears without anyone having typed it.
  const shown = typed ?? chosenLabel

  function select(value: string) {
    field.onChange(value)
    form.clearErrors(name)
    setTyped(null)
  }

  const clearOption: ComboboxOption = { value: '', label: clearLabel }

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        id={fieldId}
        label={label}
        placeholder={placeholder}
        inputValue={shown}
        onInputValueChange={setTyped}
        onSelect={select}
        loading={pending}
        loadingLabel={loadingLabel}
        emptyLabel={emptyLabel}
        disabled={pending}
        options={[clearOption, ...matching(options, shown)]}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
      />
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  )
}
